import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import GSApi from 'api/Api';
import { ChannelFilterOptionsValue, EpgProgram } from 'types/EpgTypes';
import { alignDate } from 'utils/fnEpg';
import { parseChannels, parseEpgPrograms } from 'utils/fnParser';
import translate from 'utils/fnTranslate';
import { useChannels } from 'providers/useChannels/useChannels';
import { useAuth } from 'providers/useAuth/AuthContext';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { SECONDS } from 'utils/TimeUnit';
import { useGlobalNetworkError, useGlobalNoInternet } from 'hooks/withNetworkCheck/withNetworkCheck';
import { useApp } from 'providers/useApp/AppContext';
import { healthCheckIfNotExhausted, retryHealthCheck } from 'utils/fnHealthCheck';
import { PlayerProps } from 'types/Player';
import { getLocalStorage, setLocalStorage } from 'utils/fnStorage';
import { Channel } from 'types/Asset';

const channelFetchBatchSize = 20;

const lastSelectedChannelFilterKey = 'lastSelected';

const isChannelFilterValue = (value: any): value is ChannelFilterOptionsValue => Object.values(ChannelFilterOptionsValue).includes(value);

const readStoredChannelFilter = (userId: string): ChannelFilterOptionsValue => {
    const stored = getLocalStorage('epgChannelFilter');
    const filter = stored?.[userId] ?? stored?.[lastSelectedChannelFilterKey];

    return isChannelFilterValue(filter) ? filter : ChannelFilterOptionsValue.ALL;
};

const favoritesFirst = (channelList: Channel[]): Channel[] =>
    channelList
        ? [...channelList.filter((channel) => channel.favorite), ...channelList.filter((channel) => !channel.favorite)]
        : channelList;

const storeChannelFilter = (userId: string, filter: ChannelFilterOptionsValue) => {
    setLocalStorage('epgChannelFilter', {
        ...(getLocalStorage('epgChannelFilter') ?? {}),
        ...(userId ? { [userId]: filter } : {}),
        [lastSelectedChannelFilterKey]: filter,
    });
};

const useEpgStoreService = () => {
    const [epgData, setEpgData] = useState({
        channels: [],
        programs: [],
        search: null,
    });

    const { channels, fetchChannels, loading, isFetching } = useChannels();
    const { cacheKey, isGuest, userInfoDetails } = useAuth();
    const [allChannels, setAllChannels] = useState(channels);
    const { config } = useConfig();
    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();

    const canceller = useRef(null);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [modalProgram, setModalProgram] = useState<EpgProgram | null>(null);

    const openProgramModal = useCallback((program: EpgProgram) => {
        setModalProgram((current) => (current?.id === program.id && current?.channelId === program.channelId ? current : program));
    }, []);

    const closeProgramModal = useCallback(() => setModalProgram(null), []);

    const { setNetworkError, setNoInternet } = useApp();
    const updateTimeOutRef = useRef(null);
    const backEndErrorUpdateInterval = 5000;
    const healthCheckMaxAttempts = config?.api_config.health_check_max_attempts;
    const healthCheckBackoffFactor = config?.api_config.health_check_backoff_factor;

    const userId = isGuest ? null : userInfoDetails?.sub;
    const [lineupFilter, setLineupFilter] = useState<ChannelFilterOptionsValue>(() => readStoredChannelFilter(null));
    const restoredFilterUserRef = useRef<string>(null);
    const [searchQuery, setSearchQuery] = useState<string>(null);
    const [filteredChannels, setFilteredChannels] = useState<Channel[]>(channels);

    const [playerProps, setPlayerProps] = useState<PlayerProps>(null);

    const effectiveFilter =
        lineupFilter === ChannelFilterOptionsValue.ALL || allChannels?.some((channel) => channel[lineupFilter])
            ? lineupFilter
            : ChannelFilterOptionsValue.ALL;

    const createTvBreak = (start: number, end: number): any => ({
        id: null,
        isBreak: true,
        title: translate('PLAYER_NO_INFO'),
        startTime: start,
        endTime: end,
    });

    const fillTvBreaks = (data, dayStart, dayEnd): EpgProgram[] => {
        if (!data) return [];

        let finalData = [];

        // sort programs by start time
        data = [...data.sort((programA, programB) => programA.startTime - programB.startTime)];

        // if there are no programs, create dummy item for the entire day
        if (!data?.length) {
            finalData.push(createTvBreak(dayStart, dayEnd));
        } else {
            finalData = [...data.filter((programItem) => programItem.startTime < dayEnd)];

            for (let i = 0; i < data.length; i += 1) {
                const program = data[i];
                const previousProgram = data[i - 1];

                // if the start time of the next program is before the end time of previous one
                if (previousProgram && program.startTime < previousProgram.endTime) {
                    program.startTime += previousProgram.endTime - program.startTime;
                }

                // if the gap is more then 30 seconds
                if (previousProgram && program.startTime - previousProgram.endTime > 30) {
                    finalData.splice(i + 1, 0, createTvBreak(previousProgram.endTime, program.startTime));
                }

                if (i === 0) {
                    if (program.startTime > dayStart) {
                        finalData.splice(0, 0, createTvBreak(dayStart, program.startTime));
                    }
                }

                if (typeof program.startTime === 'string') {
                    const startDateObj = new Date(program.startTime.replace('900000', ''));
                    program.startTime = startDateObj;
                }
            }

            // handle last item
            const lastProgram = finalData[finalData.length - 1];
            if (lastProgram.endTime > dayEnd) {
                lastProgram.endTime = dayEnd;
            } else {
                finalData.push(createTvBreak(lastProgram.endTime, dayEnd - 10));
            }

            // handle first item
            if (finalData[0].startTime < dayStart) {
                finalData[0].startTime = dayStart;
            }
        }

        return finalData.sort((programA, programB) => programA.startTime - programB.startTime);
    };

    const isOffline = () => !window.navigator.onLine;

    const downloadProgramsByChannels = async (channelIds: string[], dayStart: number, dayEnd: number) => {
        // clear pending requests
        if (canceller.current) {
            canceller.current();
        }

        // split into separate requests
        const channelBatches = [];
        for (let i = 0; i < channelIds.length; i += channelFetchBatchSize) {
            channelBatches.push(channelIds.slice(i, i + channelFetchBatchSize));
        }

        const fetchBatchGuide = async (batchChannelIds: string[]) => {
            let batchError = false;
            let batchResults = [];
            const broadcastsResponse = (await GSApi.fetchGuide(batchChannelIds, dayStart, dayEnd, 1, (res) => res)) as any; // TODO: !any
            const { pages, results } = broadcastsResponse.response ?? {};

            batchError = broadcastsResponse.error;
            batchResults = results ?? [];

            if (pages > 1) {
                const requests = [];

                for (let i = 1; i < pages; i += 1) {
                    requests.push(GSApi.fetchGuide(batchChannelIds, dayStart, dayEnd, i + 1, (res) => res));
                }

                const responses = await Promise.all(requests);

                responses.forEach((res) => {
                    batchResults = [...batchResults, ...(res?.response?.results ?? [])];
                    batchError = res.error;
                });
            }

            return { error: batchError, results: batchResults, status: broadcastsResponse?.status };
        };

        const batchResponses = await Promise.all(channelBatches.map(fetchBatchGuide));

        const requestError = batchResponses.some((batch) => batch.error);
        const responseStatus = Math.max(0, ...batchResponses.map((batch) => batch.status ?? 0));
        const guideResults = batchResponses.flatMap((batch) => batch.results);

        if (requestError && isOffline()) {
            updateTimeOutRef.current = window.setInterval(() => {
                if (!isOffline()) {
                    console.log('clearInterval offline');
                    clearInterval(updateTimeOutRef.current);
                    setNoInternet(false);
                }
            }, backEndErrorUpdateInterval);
            onNoInternet();
        } else if (!!requestError || responseStatus >= 400) {
            if (await healthCheckIfNotExhausted()) {
                setNetworkError(null);
                setNoInternet(false);
            } else {
                retryHealthCheck(backEndErrorUpdateInterval, healthCheckMaxAttempts, healthCheckBackoffFactor, () => setNetworkError(null));
                onNetworkError(responseStatus || -1);
            }
        } else {
            const requestResults = guideResults;
            const epgDataHelper = { ...epgData };

            if (requestResults) {
                requestResults.forEach((channelItem) => {
                    const id = channelItem.channel_id;

                    if (!epgDataHelper.programs[id]) {
                        epgDataHelper.programs[id] = {};
                    }

                    if (!epgDataHelper.programs[id][dayStart]) {
                        epgDataHelper.programs[id][dayStart] = [];
                    }

                    if (!epgDataHelper.programs[id][dayStart].some((obj) => obj.id === channelItem.id)) {
                        const channel = channels?.find((ch) => ch.id === id);
                        const parsedProgram = parseEpgPrograms(channelItem, channel);
                        epgDataHelper.programs[id][dayStart].push(parsedProgram);
                    }
                });
            }

            channelIds.forEach((id) => {
                if (!epgDataHelper.programs[id]) {
                    epgDataHelper.programs[id] = {};
                }
                const programs = epgDataHelper.programs[id][dayStart] || [];
                const filled = fillTvBreaks(programs, dayStart, dayEnd);

                epgDataHelper.programs[id][dayStart] = filled;
            });

            setEpgData({
                ...epgData,
                ...epgDataHelper,
            });
        }

        return null;
    };

    const getProgramsByChannels = async (channelIds: string[], day: number) => {
        let dayStart;

        // calculate day start
        dayStart = new Date(SECONDS.toMillis(day));
        dayStart = alignDate(dayStart, config.app_config.epg_settings.day_start_hour);

        // create day key
        const dayKey = dayStart.getTime();

        // calculate day end
        const dayEnd = new Date(dayStart);
        dayEnd.setDate(dayEnd.getDate() + 1);

        const notCachedChannels = channelIds.filter(
            (channel) => epgData.programs[channel] == null || epgData.programs[channel][dayKey] == null
        );

        // each channel from the set is cached
        if (!notCachedChannels.length) {
            return;
        }

        await downloadProgramsByChannels(notCachedChannels, dayStart.getTime(), dayEnd.getTime());
    };

    const search = (query: string) => {
        setSearchQuery(query);

        if (query != null && ((query.trim() === '' && epgData.search === '') || (query.trim() === '' && epgData.search !== ''))) {
            return setEpgData({
                ...epgData,
                ...{ search: null },
            });
        }
        setEpgData({
            ...epgData,
            ...{ search: query },
        });

        return null;
    };

    const filterChannel = (selection: ChannelFilterOptionsValue) => {
        setLineupFilter(selection);
        storeChannelFilter(userId, selection);

        setEpgData({
            ...epgData,
        });
    };

    const restoreChannelFilter = () => {
        setLineupFilter(readStoredChannelFilter(userId));
    };

    const loadChannels = (force: boolean = false) => {
        if (!channels?.length) {
            fetchChannels(force);
        }
    };

    const purgeCache = () => {
        setEpgData({
            programs: [],
            search: null,
            channels: null,
        });

        setSearchQuery(null);
    };

    useEffect(() => () => clearInterval(updateTimeOutRef.current), []);

    const lastPurgedCacheKeyRef = useRef<string | null | undefined>(undefined);

    useEffect(() => {
        if (lastPurgedCacheKeyRef.current === undefined) {
            lastPurgedCacheKeyRef.current = cacheKey;
            return;
        }

        if (cacheKey && cacheKey !== lastPurgedCacheKeyRef.current) {
            lastPurgedCacheKeyRef.current = cacheKey;
            purgeCache();
        }
    }, [cacheKey]);

    useEffect(() => {
        if (channels?.length) {
            setAllChannels(channels);
            return;
        }

        if (!channels && isFetching) {
            setAllChannels([]);
        }
    }, [channels, isFetching]);

    useEffect(() => {
        if (!userId || restoredFilterUserRef.current === userId) {
            return;
        }

        restoredFilterUserRef.current = userId;
        setLineupFilter(readStoredChannelFilter(userId));
    }, [userId]);

    useEffect(() => {
        if (channels?.length === 0) {
            setFilteredChannels(allChannels);
        }

        if (!searchQuery || searchQuery === '') {
            if (effectiveFilter === ChannelFilterOptionsValue.ALL) {
                setFilteredChannels(favoritesFirst(allChannels));
            } else {
                setFilteredChannels(favoritesFirst(allChannels.filter((channel) => channel[effectiveFilter])));
            }
        } else if (searchQuery && searchQuery.trim() !== '') {
            const filtered = allChannels.filter((channel) => channel.name.toLowerCase().includes(searchQuery.toLowerCase()));

            setFilteredChannels(favoritesFirst(filtered));
        }
    }, [effectiveFilter, searchQuery, allChannels, channels]);

    return {
        channels: parseChannels(filteredChannels),
        channelsLoading: loading || isFetching,
        programs: epgData.programs,
        searched: searchQuery,
        selectedProgram,
        loadChannels,
        getProgramsByChannels,
        search,
        purgeCache,
        setSelectedProgram,
        modalProgram,
        openProgramModal,
        closeProgramModal,
        playerProps,
        setPlayerProps,
        filterChannel,
        restoreChannelFilter,
        channelFilter: effectiveFilter,
    };
};

export const EpgDataContext = createContext<ReturnType<typeof useEpgStoreService>>(null);

export const EpgProvider = ({ children }) => {
    const epg = useEpgStoreService();
    return <EpgDataContext.Provider value={epg}>{children}</EpgDataContext.Provider>;
};

export const useEpgStore = () => useContext(EpgDataContext);
